import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import { styled } from '@mui/material/styles';

import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';

import { formatAmount } from '@/lib/formatAmount';
import { PayOptions, type PayOption } from '@/pages/dokkaPay/pages/payment/components/PaymentFooter/helpers/payOption';

type Props = {
  onPay: (option: PayOption) => void;
  option: PayOption;
  totalAmount: string;
  currencyCode: string;
  disabled?: boolean;
};

const BUTTON_LABELS = {
  [PayOptions.pay]: { id: 'payment.footer.actionButton.pay', defaultMessage: 'Pay {sum}' },
  [PayOptions.pay_and_stay]: { id: 'payment.footer.actionButton.payAndStay', defaultMessage: 'Pay and stay {sum}' },
} as const;

const MENU_ITEMS = [
  { key: PayOptions.pay, label: { id: 'payment.footer.payOption.pay', defaultMessage: 'Pay' } },
  {
    key: PayOptions.pay_and_stay,
    label: { id: 'payment.footer.payOption.payAndStay', defaultMessage: 'Pay and stay' },
  },
] as const;

const RoundedButtonGroup = styled(ButtonGroup)({
  '& .MuiButtonGroup-firstButton': { borderRadius: '20px 0 0 20px' },
  '& .MuiButtonGroup-lastButton': { borderRadius: '0 20px 20px 0' },
});

// like AP's Publish split button: picking an option from the menu also runs it
const PayButton: React.FC<Props> = ({ onPay, option, totalAmount, currencyCode, disabled }) => {
  const { formatMessage } = useIntl();
  const popupState = usePopupState({ variant: 'popover', popupId: 'PayButtonOptions' });

  const handleMainClick = useCallback(() => onPay(option), [onPay, option]);

  const handleMenuItemClick = useCallback(
    (selected: PayOption) => {
      popupState.close();
      onPay(selected);
    },
    [onPay, popupState],
  );

  return (
    <>
      <RoundedButtonGroup variant="contained" disabled={disabled}>
        <Button onClick={handleMainClick}>
          {formatMessage(BUTTON_LABELS[option], { sum: formatAmount(totalAmount, currencyCode) })}
        </Button>
        <Button
          size="small"
          aria-label={formatMessage({
            id: 'payment.footer.payOption.menu',
            defaultMessage: 'Choose what happens after paying',
          })}
          {...bindTrigger(popupState)}
        >
          {popupState.isOpen ? <ArrowDropUpOutlinedIcon /> : <ArrowDropDownOutlinedIcon />}
        </Button>
      </RoundedButtonGroup>
      <Menu
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: { mt: -1, minWidth: '18ch' } } }}
        {...bindMenu(popupState)}
      >
        {MENU_ITEMS.map(({ key, label }) => (
          <MenuItem
            key={key}
            role="menuitemradio"
            aria-checked={key === option}
            selected={key === option}
            onClick={() => handleMenuItemClick(key)}
          >
            {formatMessage(label)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PayButton;
